import * as React from 'react';
import { css } from '@emotion/react';
import type { SerializedStyles, Theme } from '@emotion/react';
import { Box, Inline, Text, Stack, Progress, useBreakpoint } from '@resi-media/resi-ui';
import { useLivePlayer } from '@sites/contexts/livePlayerContext';
import { usePrefix } from '@studio/hooks/use-prefix';

const S = {
  timeNumber: (theme: Theme) => css`
    color: ${theme.palette.common.white};
    font-size: clamp(${theme.spacing.x12}, 10vw, ${theme.spacing.x32});
    font-weight: ${theme.typography.fontWeight.bold};
  `,
  timeText: (theme: Theme) => css`
    color: ${theme.palette.common.white};
    font-size: clamp(${theme.spacing.m}, 4vw, ${theme.spacing.xl});
    font-weight: ${theme.typography.fontWeight.regular};
  `,
  wrapper: (theme: Theme) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-height: ${theme.typography.pxToRem(443)};
    max-width: ${theme.typography.pxToRem(788)};
    aspect-ratio: 16/9;
    padding: clamp(${theme.spacing.s}, 4%, ${theme.typography.pxToRem(101)})
      clamp(${theme.spacing.x3}, 6%, ${theme.typography.pxToRem(146)});
    box-sizing: border-box;
    background: ${theme.palette.common.black};
    color: ${theme.palette.common.white};
  `,
};

type LivePlayerCountdownProps = {
  customCss?: SerializedStyles;
};

const LivePlayerCountdown = ({ customCss }: LivePlayerCountdownProps) => {
  const { countdownSeconds } = useLivePlayer();
  const { commonT, prefixNS } = usePrefix('components:', 'livePlayerCountdown');
  const mediaQuery = useBreakpoint();

  const hrs = String(Math.floor(countdownSeconds / 3600)).padStart(2, '0');
  const mins = String(Math.floor((countdownSeconds % 3600) / 60)).padStart(2, '0');
  const secs = String(countdownSeconds % 60).padStart(2, '0');

  return (
    <Box css={[S.wrapper, customCss]} dataTestId="live-player-countdown">
      {countdownSeconds > 0 ? (
        <Inline dataTestId="time-text-container" gap="l">
          <Stack>
            <Text css={S.timeNumber}>{hrs}</Text>
            <Text css={S.timeText} textAlign="center">
              {commonT('hour_other')}
            </Text>
          </Stack>
          <Stack>
            <Text css={S.timeNumber}>{mins}</Text>
            <Text css={S.timeText} textAlign="center">
              {commonT('minute_other')}
            </Text>
          </Stack>
          <Stack>
            <Text css={S.timeNumber}>{secs}</Text>
            <Text css={S.timeText} textAlign="center">
              {commonT('second_other')}
            </Text>
          </Stack>
        </Inline>
      ) : (
        <Stack dataTestId="loading-countdown" gap={{ xs: 's', lg: 'x9' }}>
          <Progress colorVariant="inactive" sizeVariant={mediaQuery.lg ? 'm' : 's'} />
          <Text color="heading" variant={mediaQuery.lg ? 'h1' : 'h4'}>
            {prefixNS('liveBroadcastStartingSoon')}
          </Text>
        </Stack>
      )}
    </Box>
  );
};

LivePlayerCountdown.displayName = 'LivePlayerCountdown';

export { LivePlayerCountdown };
